"use client";

import {
  getAnswer,
  intakeFormToQuestionnaire,
  type MoneyValue,
  type QuestionnaireFileValue,
} from "@repo/questionnaire";

import { QuestionnaireFieldRenderer } from "@/components/questionnaire";
import type { BusinessTypeOption } from "@/components/listings/business-type-combobox";

export type InquiryForm = {
  id?: string;
  title: string;
  description: string | null;
  token?: string;
  currentVersion?: number;
  sections: Array<{
    id: string;
    name: string;
    description?: string | null;
    fields: Array<{
      id: string;
      fieldKey: string;
      label: string;
      kind: string;
      required: boolean;
      bindingKey?: string | null;
      options: unknown;
      allowMultiple: boolean;
      settings?: unknown;
    }>;
  }>;
};

type InquiryResponse =
  | string
  | string[]
  | boolean
  | MoneyValue
  | BusinessTypeOption
  | QuestionnaireFileValue
  | QuestionnaireFileValue[];

export type { InquiryResponse };

export function InquiryIntakeFields({
  form,
  responses,
  onChange,
}: {
  form: InquiryForm;
  responses: Record<string, InquiryResponse>;
  onChange: (responses: Record<string, InquiryResponse>) => void;
}) {
  const definition = intakeFormToQuestionnaire({
    formId: form.id ?? form.token ?? "inquiry",
    version: form.currentVersion ?? 1,
    title: form.title,
    description: form.description,
    sections: form.sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => ({
        ...field,
        required: false,
      })),
    })),
    excludeFieldKeys: ["full_name", "name", "email", "phone"],
  });

  return (
    <div className="space-y-5 rounded-xl border border-border/70 bg-muted/20 p-4">
      <div>
        <p className="text-sm font-medium">{form.title}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Add any details that will help the broker understand how to help.
        </p>
      </div>
      {definition.steps.map((step) => (
        <div key={step.id} className="space-y-3">
          <p className="text-xs font-semibold text-muted-foreground">
            {step.title}
          </p>
          {step.fields.map((field) => (
            <QuestionnaireFieldRenderer
              key={field.id}
              field={field}
              answers={responses}
              value={getAnswer(responses, field.binding)}
              intakeToken={form.token}
              onChange={(value) =>
                onChange({
                  ...responses,
                  [field.binding]: value as InquiryResponse,
                })
              }
            />
          ))}
        </div>
      ))}
    </div>
  );
}
